import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Avatar,
  Divider,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  ArrowBack as ArrowBackIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  LocalShipping as LocalShippingIcon,
  Payment as PaymentIcon,
  ContentCopy as ContentCopyIcon,
  WhatsApp as WhatsAppIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Language as LanguageIcon,
  Visibility as VisibilityIcon,
  CalendarToday as CalendarTodayIcon,
  FmdGood as FmdGoodIcon,
} from "@mui/icons-material";
import { Menu } from "@mui/material";
import api from "../../config/axios";
import { motion } from "framer-motion";
//import OrderPrint from "../Print/OrderPrint";

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const OrderDetails = () => {
  const { orderId: id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [address, setAddress] = useState(null);
  const [addressLoading, setAddressLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [templates, setTemplates] = useState([]);

  const isMobile = useMediaQuery("(max-width: 600px)");

  // Modal States
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [labelModalOpen, setLabelModalOpen] = useState(false);
  const [deleteAddressDialogOpen, setDeleteAddressDialogOpen] = useState(false);
  const [deleteAddressLoading, setDeleteAddressLoading] = useState(false);
  const [courierByTrackingId, setCourierByTrackingId] = useState({});
  const [trackingModalOpen, setTrackingModalOpen] = useState(false);
  const [trackingUrl, setTrackingUrl] = useState("");
  const [delhiveryData, setDelhiveryData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [itemPrices, setItemPrices] = useState({});
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);

  useEffect(() => {
    fetchOrderDetails();
    fetchProducts();
    fetchCouriers();
    fetchTemplates();
  }, [id]);

  const fetchOrderDetails = async () => {
    try {
      const response = await api.get(
        `/orders.php?id=${id}`
      );
      if (response.data.success && response.data.data.orders && response.data.data.orders.length > 0) {
        const orderData = response.data.data.orders[0];
        // Standardize status field if backend returns it as 'status'
        if (orderData.status && !orderData.status) {
          orderData.status = orderData.status;
        }

        let initialProducts = [];
        if (orderData.product_name) {
          if (Array.isArray(orderData.product_name)) {
            initialProducts = orderData.product_name;
          } else if (typeof orderData.product_name === 'string') {
            try {
              const parsed = JSON.parse(orderData.product_name);
              initialProducts = Array.isArray(parsed) ? parsed : [parsed];
            } catch (e) {
              if (orderData.product_name.includes(',')) {
                initialProducts = orderData.product_name.split(',').map(s => s.trim()).filter(Boolean);
              } else {
                initialProducts = [orderData.product_name];
              }
            }
          }
        }

        setOrder(orderData);
        setEditFormData({
          ...orderData,
          product_name: initialProducts,
          remark: orderData.remark || "",
        });
        fetchAddressDetails(orderData.id);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching order details:", error);
      setLoading(false);
    }
  };

  const fetchAddressDetails = async (orderId) => {
    setAddressLoading(true);
    try {
      const response = await api.get(
        `/get_address.php?order_id=${orderId}`
      );
      if (response.data.success) {
        setAddress(response.data.data);
      } else {
        setAddress(null);
      }
    } catch (error) {
      console.error("Error fetching address details:", error);
    } finally {
      setAddressLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get(
        "/products.php"
      );
      if (response.data.success) {
        setProducts(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchCouriers = async () => {
    try {
      const response = await api.get(
        "/courier_partners.php"
      );
      if (response.data.success) {
        setCouriers(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching couriers:", error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get(
        "/templates.php"
      );
      if (response.data.success) {
        setTemplates(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase();
    if (s === "dispatched" || s === "ready-to-ship") return { background: "#B76E00", color: "#FFFFFF", border: 'none' };
    if (s === "delivered" || s === "accepted") return { background: "#1E7E34", color: "#FFFFFF", border: 'none' };
    if (s === "pending-approval" || s === "in-progress") return { background: "#0056D2", color: "#FFFFFF", border: 'none' };
    if (s === "canceled") return { background: "#C62828", color: "#FFFFFF", border: 'none' };
    return { background: "#616161", color: "#FFFFFF", border: 'none' };
  };

  const getPaymentStatusColor = (status) => {
    const s = status?.toLowerCase();
    if (s === "paid") return { background: "#1E7E34", color: "#FFFFFF", border: 'none' };
    if (s === "paid-advance") return { background: "#B76E00", color: "#FFFFFF", border: 'none' };
    if (s === "pending") return { background: "#C62828", color: "#FFFFFF", border: 'none' };
    return { background: "#616161", color: "#FFFFFF", border: 'none' };
  };

  const formatStatus = (status) => {
    if (!status) return "N/A";
    return status
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleEditInputChange = (field, value) => {
    setEditFormData({ ...editFormData, [field]: value });
  };

  const handleEditSubmit = async () => {
    setEditLoading(true);
    try {
      // Strictly construct payload with only allowed fields
      const payload = {
        id: order.id,
        product_name: JSON.stringify(editFormData.product_name),
        courier_id: editFormData.courier_id,
        phone: editFormData.phone,
        email: editFormData.email,
        dom: editFormData.dom,
        bride_name: editFormData.bride_name,
        groom_name: editFormData.groom_name,
        display_bride_name: editFormData.display_bride_name,
        display_groom_name: editFormData.display_groom_name,
        short_display_name: editFormData.short_display_name,
        advance_amount: editFormData.advance_amount,
        order_amount: editFormData.order_amount,
        profit: editFormData.profit,
        remark: editFormData.remark || "",
        payment_status: editFormData.payment_status,
        status: editFormData.status,
        payment_mode: editFormData.payment_mode || "",
        color: editFormData.color || "",
        template_id: editFormData.template_id || "",
        tracking_id: editFormData.tracking_id || ""
      };

      const response = await api.put(
        `/orders.php?id=${order.id}`,
        payload
      );
      if (response.data.success) {
        setEditModalOpen(false);
        fetchOrderDetails();
      }
    } catch (error) {
      console.error("Error updating order:", error);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteAddress = async () => {
    setDeleteAddressLoading(true);
    try {
      const response = await api.post(
        "/delete_address.php",
        { order_id: order.id }
      );
      if (response.data.status === "success") {
        setDeleteAddressDialogOpen(false);
        setAddress(null);
      }
    } catch (error) {
      console.error("Error deleting address:", error);
    } finally {
      setDeleteAddressLoading(false);
    }
  };



  const sendWhatsAppMessage = (phone, message) => {
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phone}?text=${encodedMessage}`, "_blank");
  };




  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleSendDispatchEmail = async (type = 'dispatch') => {
    if (!order?.email && !address?.email) {
      alert("No customer email found for this order.");
      return;
    }

    setEmailLoading(type);
    try {
      const response = await api.post('/send_dispatch_email.php', {
        order_id: order.id,
        tracking_id: order.tracking_id,
        type: type
      });

      if (response.data.success) {
        alert(`${type === 'dispatch' ? 'Dispatch' : 'Out for delivery'} email sent successfully!`);
      } else {
        alert("Failed to send email: " + (response.data.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Error sending email:", error);
      alert("An error occurred while sending the email.");
    } finally {
      setEmailLoading(false);
    }
  };

  const handlePrintOpen = () => {
    setPrintModalOpen(true);
  };

  const handlePrintClose = () => {
    setPrintModalOpen(false);
  };

  const handlePrintConfirm = (contentId = "print-content") => {
    const printContent = document.getElementById(contentId);
    const originalContent = document.body.innerHTML;
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  const handleOpenEditModal = () => {
    let initialProducts = [];
    if (order?.product_name) {
      if (Array.isArray(order.product_name)) {
        initialProducts = order.product_name;
      } else if (typeof order.product_name === 'string') {
        try {
          const parsed = JSON.parse(order.product_name);
          initialProducts = Array.isArray(parsed) ? parsed : [parsed];
        } catch (e) {
          if (order.product_name.includes(',')) {
            initialProducts = order.product_name.split(',').map(s => s.trim()).filter(Boolean);
          } else {
            initialProducts = [order.product_name];
          }
        }
      }
    }
    setEditFormData({
      ...order,
      product_name: initialProducts,
      remark: order?.remark || "",
    });
    setEditModalOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", flexDirection: "column", gap: 2 }}>
        <CircularProgress sx={{ color: "primary.main" }} />
        <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
          Unveiling Order Details...
        </Typography>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ p: 4, textAlign: "center", minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 3 }}>
        <Typography variant="h4" sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 900 }}>Artistry not found.</Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 600 }}>This masterpiece seems to have been moved or archived.</Typography>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mt: 2, borderRadius: '12px', px: 4, fontWeight: 800 }}>
          Back
        </Button>
      </Box>
    );
  }

  const productsInOrder = order.product_name ? JSON.parse(order.product_name) : [];

  return (
    <>
      <Box sx={{ p: { xs: 2.5, md: 4.5 }, maxWidth: "1600px", mx: "auto" }}>
        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          {/* Action Bar */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 5 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ color: "text.primary", fontWeight: 800, textTransform: "none", borderRadius: "12px", px: 2 }}
            >
              Back
            </Button>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleOpenEditModal}
                sx={{
                  background: "linear-gradient(135deg, #B51D6E 0%, #E91E63 100%)",
                  color: "#FFFFFF !important",
                  fontWeight: 900,
                  textTransform: "none",
                  borderRadius: "12px",
                  px: { xs: 2, sm: 3 },
                  boxShadow: '0 4px 12px rgba(181, 29, 110, 0.2)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #E91E63 0%, #B51D6E 100%)',
                  }
                }}
              >
                {!isMobile && "Update Order"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => setPrintModalOpen(true)}
                sx={{ borderColor: "rgba(0,0,0,0.1)", color: 'primary.main', fontWeight: 800, textTransform: "none", borderRadius: "12px", px: { xs: 2, sm: 3 }, borderWidth: '2px' }}
              >
                {!isMobile && "Generate Bill"}
              </Button>
              <IconButton
                onClick={(e) => setActionMenuAnchor(e.currentTarget)}
                sx={{
                  bgcolor: 'rgba(0,0,0,0.03)',
                  borderRadius: '12px',
                  p: 1.5,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
                }}
              >
                <MoreVertIcon />
              </IconButton>
            </Stack>
          </Box>

          <Menu
            anchorEl={actionMenuAnchor}
            open={Boolean(actionMenuAnchor)}
            onClose={() => setActionMenuAnchor(null)}
            PaperProps={{
              sx: {
                borderRadius: '16px',
                mt: 1,
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                minWidth: 260,
              }
            }}
          >
            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              <Typography variant="caption" sx={{ fontWeight: 900, color: 'text.secondary', textTransform: 'uppercase' }}>WhatsApp Actions</Typography>
            </Box>
            <MenuItem
              onClick={() => {
                setActionMenuAnchor(null);
                const customerName = order.display_bride_name || order.bride_name || 'Client';
                const msg = `Hi ${customerName},\n\nThank you for choosing Ishqnama Art! We have received your order (#${order.order_id}).\n\nWe will update you once it is ready.`;
                sendWhatsAppMessage(order.phone, msg);
              }}
              sx={{ fontWeight: 600, py: 1.5 }}
            >
              Order Registration
            </MenuItem>
            <MenuItem
              onClick={() => {
                setActionMenuAnchor(null);
                const customerName = order.display_bride_name || order.bride_name || 'Client';
                const trackingLink = order.tracking_id ? `https://ishqnamaart.in/track-order?id=${order.order_id}` : `https://ishqnamaart.in/track-order`;
                const msg = `Hi ${customerName},\n\nYour order (#${order.order_id}) has been dispatched!\nKeep track here: ${trackingLink}`;
                sendWhatsAppMessage(order.phone, msg);
              }}
              sx={{ fontWeight: 600, py: 1.5 }}
            >
              Order Dispatched
            </MenuItem>
            <MenuItem
              onClick={() => {
                setActionMenuAnchor(null);
                const customerName = order.display_bride_name || order.bride_name || 'Client';
                const trackingLink = `https://ishqnamaart.in/track-order?id=${order.order_id}`;
                const msg = `Hi ${customerName},\n\nGood news! 🚚\nYour order is out for delivery and will reach you soon.\n\nYou can track your order here:\n${trackingLink}\n\nPlease keep your phone reachable for a smooth delivery.\n\nThank you for shopping with us!`;
                sendWhatsAppMessage(order.phone, msg);
              }}
              sx={{ fontWeight: 600, py: 1.5 }}
            >
              Send out for delivery message
            </MenuItem>
            <MenuItem
              onClick={() => {
                setActionMenuAnchor(null);
                const customerName = order.display_bride_name || order.bride_name || 'Client';
                const reorderLink = `https://ishqnamaart.in/products`;
                const msg = `Hi ${customerName},\n\nWe regret to inform you that your order has been cancelled.\n\nIf you’d like to place a new order, you can do so here:\n${reorderLink}\n\nFor any questions, feel free to reach out—we’re happy to help!`;
                sendWhatsAppMessage(order.phone, msg);
              }}
              sx={{ fontWeight: 600, py: 1.5 }}
            >
              Send order cancel message
            </MenuItem>
          </Menu>

          {/* Main Card */}
          <motion.div variants={itemVariants}>
            <Card sx={{ borderRadius: "32px", overflow: "hidden", border: "1px solid rgba(0,0,0,0.05)", boxShadow: "0 20px 40px rgba(0,0,0,0.04)", mb: 4 }}>
              <Box sx={{ p: 3, background: "linear-gradient(135deg, #6355e3ff, #f9cdeeff)", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: 'white', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    #{order.order_id}
                  </Typography>
                  <Chip
                    label={formatStatus(order.status)}
                    sx={{ ...getStatusColor(order.status), fontWeight: 800, fontSize: "0.75rem", height: "26px" }}
                  />
                  {(order.razorpay_order_id || order.razorpay_payment_id) && (
                    <Chip
                      label={!isMobile ? "WEBSITE ORDER" : ""}
                      size="small"
                      icon={<LanguageIcon />}
                      sx={{
                        background: "linear-gradient(135deg, #001373ff 0%, #c8006aff 100%)",
                        color: "#FFFFFF",
                        fontWeight: 800,
                        fontSize: "0.75rem",
                        height: "26px",
                        border: 'none',
                        '& .MuiChip-icon': {
                          color: 'inherit',
                          fontSize: '0.85rem',
                          marginRight: !isMobile ? undefined : '0px',
                          marginLeft: !isMobile ? undefined : '4px'
                        },
                        '& .MuiChip-label': {
                          display: !isMobile ? 'block' : 'none'
                        },
                        minWidth: !isMobile ? 'auto' : '30px'
                      }}
                    />
                  )}
                </Box>
              </Box>

              <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                <Grid container spacing={5}>
                  {/* Customer Identity */}
                  <Grid item xs={12} md={5}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3, mb: 4 }}>
                      <Avatar sx={{ width: 80, height: 80, background: "#B51D6E", fontSize: "1.75rem", fontWeight: 900, fontFamily: "'Playfair Display', serif" }}>
                        {order.bride_name?.charAt(0)}{order.groom_name?.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="h5" sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, mb: 0.5 }}>
                          {order.display_bride_name} & {order.display_groom_name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>Bride: {order.bride_name} | Groom: {order.groom_name}</Typography>
                      </Box>
                    </Box>

                    <Stack spacing={2} sx={{ p: 3, borderRadius: '16px', background: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.03)' }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <PhoneIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{order.phone}</Typography>
                        <IconButton size="small" onClick={() => copyToClipboard(order.phone)} sx={{ ml: -1 }}>
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                      {order.email && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <EmailIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{order.email}</Typography>
                        </Box>
                      )}
                      {order.address && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <LocationOnIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.5 }}>{order.address}</Typography>
                        </Box>
                      )}
                    </Stack>
                  </Grid>

                  {/* Order Specifications */}
                  <Grid item xs={12} md={7}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800, textTransform: 'uppercase', display: 'block', mb: 1 }}>Marriage Date</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>{formatDate(order.dom)}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800, textTransform: 'uppercase', display: 'block', mb: 1 }}>Order Date</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>{formatDate(order.created_at)}</Typography>
                      </Grid>
                      {order.remark && (
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800, textTransform: 'uppercase', display: 'block', mb: 1 }}>Notes</Typography>
                          <Box
                            sx={{
                              maxWidth: "120px",
                              backgroundColor: "#FEF3C7",
                              border: "1px solid #FDE68A",
                              borderRadius: "8px",
                              p: 1.25,
                              boxShadow: "0 2px 8px rgba(245, 158, 11, 0.15)",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontStyle: "italic",
                                color: "#92400E",
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                lineHeight: 1.4,
                              }}
                            >
                              {order.remark}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800, textTransform: 'uppercase', display: 'block', mb: 1 }}>Commissioned Pieces</Typography>
                        <Stack spacing={1}>
                          {productsInOrder.map((piece, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box sx={{ width: 20, height: 20, borderRadius: '4px', bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 900 }}>{i + 1}</Box>
                              {String(piece).startsWith("2026") ? (
                                <Typography
                                  variant="body2"
                                  component="a"
                                  href={`https://ishqnamaart.in/product/${piece}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ fontWeight: 700, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                  {piece}
                                </Typography>
                              ) : (
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{piece}</Typography>
                              )}
                            </Box>
                          ))}
                        </Stack>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </motion.div>

          {/* Secondary Cards: Financials, Transit, and Delivery Address */}
          <motion.div variants={itemVariants}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 3,
                width: "100%",
                alignItems: "stretch",
              }}
            >
              {/* Financials Card */}
              <Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>
                <Card sx={{ borderRadius: '24px', width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <PaymentIcon sx={{ color: 'primary.main' }} /> Financials
                      </Typography>
                      <IconButton size="small" onClick={() => sendWhatsAppMessage(order.phone, "Your financial summary...")} sx={{ color: "#25D366" }}>
                        <WhatsAppIcon />
                      </IconButton>
                    </Box>
                    <Stack spacing={3}>
                      <Box sx={{ display: "flex", justifyContent: 'space-between', p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: '12px' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>Payment Status: </Typography>
                        <Chip label={formatStatus(order.payment_status)} sx={{ ...getPaymentStatusColor(order.payment_status), fontWeight: 800, height: 24 }} />
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="text.secondary">Order Amount</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 800 }}>₹{order.order_amount?.toLocaleString()}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="text.secondary">Advance Paid</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.main' }}>₹{order.advance_amount?.toLocaleString()}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="text.secondary">Direct Profit</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 800, color: '#B51D6E' }}>₹{order.profit?.toLocaleString() || 0}</Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body1" sx={{ fontWeight: 800 }}>COD Balance</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 900, color: 'primary.main' }}>₹{((order.order_amount || 0) - (order.advance_amount || 0)).toLocaleString()}</Typography>
                      </Box>
                      {(order.razorpay_order_id || order.razorpay_payment_id) && (
                        <>
                          <Divider />
                          <Typography variant="caption" sx={{ fontWeight: 800, color: '#B51D6E', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: -1 }}>Razorpay Details</Typography>
                          {order.razorpay_order_id && (
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography variant="body2" color="text.secondary">Order ID</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{order.razorpay_order_id}</Typography>
                            </Box>
                          )}
                          {order.razorpay_payment_id && (
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography variant="body2" color="text.secondary">Payment ID</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{order.razorpay_payment_id}</Typography>
                            </Box>
                          )}
                        </>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Box>

              {/* Transit Card */}
              <Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>
                <Card sx={{ borderRadius: '24px', width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <LocalShippingIcon sx={{ color: 'primary.main' }} /> Transit
                      </Typography>
                      <IconButton size="small" onClick={() => sendWhatsAppMessage(order.phone, "Tracking for your order...")} sx={{ color: "#25D366" }}>
                        <WhatsAppIcon />
                      </IconButton>
                    </Box>
                    <Stack spacing={3}>
                      <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: '12px' }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', display: 'block', mb: 1 }}>Awb Number</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 800, fontFamily: 'monospace' }}>{order.tracking_id || "Not Assigned"}</Typography>
                          {order.tracking_id && <IconButton size="small" onClick={() => copyToClipboard(order.tracking_id)}><ContentCopyIcon sx={{ fontSize: 14 }} /></IconButton>}
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', display: 'block', mb: 0.5 }}>Courier Partner</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>{order.courier_name || "Self Dispatched"}</Typography>
                      </Box>
                      {order.tracking_id && (
                        <Stack spacing={1}>
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={async () => {
                              const courierName = (order.courier_name || "").toLowerCase();
                              const trackingId = order.tracking_id;

                              if (courierName.includes("shipway")) {
                                setDelhiveryData(null);
                                setTrackingUrl(`https://ishqnama.shipway.com/tracking/forward/${trackingId}/`);
                                setTrackingModalOpen(true);
                              } else if (courierName.includes("delhivery")) {
                                setTrackingLoading(true);
                                setTrackingModalOpen(true);
                                setTrackingUrl("");
                                try {
                                  const response = await api.get(`/delhivery_tracking.php`, {
                                    params: { waybill: trackingId }
                                  });
                                  if (response.data.success) {
                                    setDelhiveryData(response.data.data);
                                  }
                                } catch (error) {
                                  console.error("Error fetching Delhivery tracking:", error);
                                } finally {
                                  setTrackingLoading(false);
                                }
                              } else {
                                setDelhiveryData(null);
                                setTrackingUrl(`https://shiprocket.co/tracking/${trackingId}`);
                                setTrackingModalOpen(true);
                              }
                            }}
                            sx={{ background: '#B51D6E', borderRadius: '12px', fontWeight: 900, color: "#FFFFFF !important" }}
                          >
                            Track Shipment
                          </Button>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<EmailIcon />}
                            onClick={() => handleSendDispatchEmail('dispatch')}
                            disabled={(!order.email && !address?.email) || emailLoading}
                            sx={{
                              borderRadius: '12px',
                              fontWeight: 800,
                              borderColor: 'primary.main',
                              color: 'primary.main',
                              '&:hover': { border: '1px solid', borderColor: 'primary.dark' }
                            }}
                          >
                            {emailLoading === 'dispatch' ? <CircularProgress size={20} /> : "Send Dispatch Email"}
                          </Button>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<LocalShippingIcon />}
                            onClick={() => handleSendDispatchEmail('out_for_delivery')}
                            disabled={(!order.email && !address?.email) || emailLoading}
                            sx={{
                              borderRadius: '12px',
                              fontWeight: 800,
                              borderColor: '#1E7E34',
                              color: '#1E7E34',
                              '&:hover': { border: '1px solid', borderColor: '#155724', bgcolor: 'rgba(30, 126, 52, 0.04)' }
                            }}
                          >
                            {emailLoading === 'out_for_delivery' ? <CircularProgress size={20} /> : "Send Out for Delivery"}
                          </Button>
                        </Stack>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Box>

              {/* Delivery Address Card */}
              <Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>
                <Card sx={{ borderRadius: '24px', width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <LocationOnIcon sx={{ color: 'primary.main' }} /> Delivery Address
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<PrintIcon />}
                        onClick={() => setLabelModalOpen(true)}
                        sx={{ textTransform: 'none', fontWeight: 800, color: 'primary.main' }}
                      >
                        Print Label
                      </Button>
                    </Box>
                    {address ? (
                      <Stack spacing={2}>
                        <Box sx={{ p: 2.5, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.04)' }}>
                          <Box sx={{ mb: 2, pb: 2, borderBottom: '1px dashed rgba(0,0,0,0.08)' }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#B51D6E', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.5 }}>Recipient Profile</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 900, fontSize: '1.1rem', mb: 0.25 }}>{address.recipient_name}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>{address.phone_number}</Typography>
                              <IconButton size="small" onClick={() => copyToClipboard(address.phone_number)} sx={{ ml: -1 }}>
                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                            {address.email && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>{address.email}</Typography>
                                <IconButton size="small" onClick={() => copyToClipboard(address.email)} sx={{ ml: -1 }}>
                                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: '#B51D6E', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.5 }}>Delivery Coordinates</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.6, color: '#1a1a1a' }}>
                            {address.street_address}, <br />
                            {address.city}, {address.state} - {address.postal_code}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                          <Button
                            color="error"
                            variant="outlined"
                            fullWidth
                            onClick={() => setDeleteAddressDialogOpen(true)}
                            startIcon={<DeleteIcon />}
                            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px' }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </Stack>
                    ) : (
                      <Box sx={{ py: 4, textAlign: 'center', bgcolor: 'rgba(0,0,0,0.02)', borderRadius: '16px' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, fontStyle: 'italic', mb: 2 }}>No address curated</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<WhatsAppIcon />}
                            onClick={() => {
                              const customerName = order.display_bride_name || order.bride_name || 'Client';
                              const url = `https://ishqnamaart.in/add-address/${order.id}`;
                              const message = `Hi ${customerName},\n\nCould you please share your complete delivery address along with your contact number using the link below:\n\n${url}\n\nThis will help us process your order smoothly.\n\nThank you!`;
                              sendWhatsAppMessage(order.phone, message);
                            }}
                            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 800 }}
                          >
                            Request
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => {
                              const customerName = order.display_bride_name || order.bride_name || 'Client';
                              const url = `https://ishqnamaart.in/add-address/${order.id}`;
                              const message = `Hi ${customerName},\n\nCould you please share your complete delivery address along with your contact number using the link below:\n\n${url}\n\nThis will help us process your order smoothly.\n\nThank you!`;
                              navigator.clipboard.writeText(message);
                            }}
                            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 800 }}
                          >
                            Copy
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </motion.div>
        </motion.div>
      </Box>

      {/* Edit Dialog */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
        <DialogTitle sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: '1.75rem' }}>Update Order</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {/* Customer Details */}
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, fontFamily: "'Playfair Display', serif" }}>Customer Details</Typography>
          <Grid spacing={2} sx={{ mb: 4, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Phone" value={editFormData.phone || ''} onChange={(e) => handleEditInputChange('phone', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Email" value={editFormData.email || ''} onChange={(e) => handleEditInputChange('email', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Bride Name" value={editFormData.bride_name || ''} onChange={(e) => handleEditInputChange('bride_name', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Groom Name" value={editFormData.groom_name || ''} onChange={(e) => handleEditInputChange('groom_name', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Display Bride Name" value={editFormData.display_bride_name || ''} onChange={(e) => handleEditInputChange('display_bride_name', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Display Groom Name" value={editFormData.display_groom_name || ''} onChange={(e) => handleEditInputChange('display_groom_name', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Short Display Name" value={editFormData.short_display_name || ''} onChange={(e) => handleEditInputChange('short_display_name', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Date of Marriage" InputLabelProps={{ shrink: true }} value={editFormData.dom || ''} onChange={(e) => handleEditInputChange('dom', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Order Status</InputLabel>
                <Select value={editFormData.status || ''} onChange={(e) => handleEditInputChange('status', e.target.value)} label="Order Status">
                  <MenuItem value="pending-approval">Pending Approval</MenuItem>
                  <MenuItem value="accepted">Accepted</MenuItem>
                  <MenuItem value="in-progress">In Progress</MenuItem>
                  <MenuItem value="ready-to-ship">Ready to Ship</MenuItem>
                  <MenuItem value="dispatched">Dispatched</MenuItem>
                  <MenuItem value="delivered">Delivered</MenuItem>
                  <MenuItem value="canceled">Canceled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Product Components</InputLabel>
                <Select
                  multiple
                  value={editFormData.product_name || []}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleEditInputChange('product_name', typeof val === 'string' ? val.split(',') : val);
                  }}
                  input={<OutlinedInput label="Product Components" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const product = products.find(p => p.name === value || p.id.toString() === value.toString());
                        return (
                          <Chip
                            key={value}
                            label={product ? product.name : value}
                            size="small"
                            onDelete={() => {
                              const updated = (editFormData.product_name || []).filter(item => item !== value);
                              handleEditInputChange('product_name', updated);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {products.map((product) => (
                    <MenuItem key={product.id} value={product.name}>
                      {product.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Special Remark"
                value={editFormData.remark || ''}
                onChange={(e) => handleEditInputChange('remark', e.target.value)}
                placeholder="Add any special instructions or notes here..."
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 4 }} />

          {/* Payment Details */}
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, fontFamily: "'Playfair Display', serif" }}>Payment Details</Typography>
          <Grid spacing={2} sx={{ mb: 4, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Status</InputLabel>
                <Select value={editFormData.payment_status || ''} onChange={(e) => handleEditInputChange('payment_status', e.target.value)} label="Payment Status">
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="paid-advance">Paid Advance</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth type="number" label="Order Amount" value={editFormData.order_amount || 0} onChange={(e) => handleEditInputChange('order_amount', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth type="number" label="Advance Amount" value={editFormData.advance_amount || 0} onChange={(e) => handleEditInputChange('advance_amount', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Profit" type="number" value={editFormData.profit || 0} onChange={(e) => handleEditInputChange('profit', e.target.value)} /></Grid>
          </Grid>

          <Divider sx={{ mb: 4 }} />

          {/* Shipping Details */}
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, fontFamily: "'Playfair Display', serif" }}>Shipping Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Courier Partner</InputLabel>
                <Select
                  value={editFormData.courier_id || (couriers.find(c => c.name === editFormData.courier_name)?.id) || ''}
                  onChange={(e) => handleEditInputChange('courier_id', e.target.value)}
                  label="Courier Partner"
                >
                  <MenuItem value="">None</MenuItem>
                  {couriers.map((courier) => (
                    <MenuItem key={courier.id} value={courier.id}>{courier.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Awb Tracking Id" value={editFormData.tracking_id || ''} onChange={(e) => handleEditInputChange('tracking_id', e.target.value)} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 4 }}>
          <Button onClick={() => setEditModalOpen(false)} sx={{ fontWeight: 800 }}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSubmit} disabled={editLoading} sx={{ background: '#B51D6E', borderRadius: '12px', px: 4, fontWeight: 900, color: "#FFFFFF !important" }}>
            {editLoading ? <CircularProgress size={20} color="inherit" /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Print PDF Dialog */}
      <Dialog open={printModalOpen} onClose={handlePrintClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
        <DialogTitle sx={{
          fontFamily: "'Playfair Display', serif",
          fontWeight: 900,
          fontSize: '1.75rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          Bill generate
          <IconButton onClick={handlePrintClose}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4, bgcolor: '#fcfcfc' }}>
          <Box id="print-content" sx={{
            p: '30px',
            bgcolor: '#FFFFFF',
            fontFamily: "'Inter', sans-serif",
            color: '#1a1a1a',
            width: '210mm',
            minHeight: '297mm',
            margin: 'auto',
            '@media print': {
              p: '5mm',
              width: '100%',
              boxShadow: 'none',
              border: 'none',
            }
          }}>
            {/* Bill Header - Brand Logos */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, pb: 2, borderBottom: '2px solid #B51D6E' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box component="img" src="/pink_logo.png" alt="Logo" sx={{ height: 50 }} />
                <Box component="img" src="/namelogo.png" alt="Ishqnama" sx={{ height: 35 }} />
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h5" sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, color: '#B51D6E', mb: 0 }}>TAX INVOICE</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#1a1a1a', my: 0.5 }}>Order #{order.order_id}</Typography>
                <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.1em' }}>ISSUED TO: {order.bride_name} & {order.groom_name}</Typography>
              </Box>
            </Box>

            <Grid container spacing={4} justifyContent="space-between">
              {/* Bilateral Split - Details */}
              <Grid item xs={6}>
                <Typography variant="overline" sx={{ fontWeight: 900, color: '#B51D6E', display: 'block', mb: 0.5, letterSpacing: '0.1em', fontSize: '0.65rem' }}>SHIPPING DESTINATION</Typography>
                {address ? (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.5 }}>{address.recipient_name}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#4B5563', fontSize: '0.8rem', mb: 0.5 }}>{address.phone_number}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#4B5563', lineHeight: 1.5, fontSize: '0.75rem' }}>
                      {address.street_address}, {address.city}, {address.state} - {address.postal_code}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', fontSize: '0.75rem' }}>Coordinates pending curation.</Typography>
                )}
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="overline" sx={{ fontWeight: 900, color: '#B51D6E', display: 'block', mb: 0.5, letterSpacing: '0.1em', fontSize: '0.65rem' }}>OPERATIONAL LOGS</Typography>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5, fontSize: '1rem' }}>#{order.order_id}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#4B5563', fontSize: '0.8rem' }}>Order Date: {formatDate(order.created_at)}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#4B5563', fontSize: '0.8rem' }}>Marriage Date: {formatDate(order.dom)}</Typography>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, mt: 4 }}>
              {/* Product Details Table */}
              <Box sx={{ width: '100%' }}>
                <Typography variant="overline" sx={{ fontWeight: 900, color: '#B51D6E', display: 'block', mb: 1, letterSpacing: '0.1em', fontSize: '0.65rem' }}>ORDER #{order.order_id} COMPONENTS</Typography>
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '12px', border: '1px solid rgba(0,0,0,0.05)' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#F9FAFB' }}>
                        <TableCell sx={{ fontWeight: 900, textTransform: 'uppercase', fontSize: '0.65rem', py: 1.5 }}>Sl.</TableCell>
                        <TableCell sx={{ fontWeight: 900, textTransform: 'uppercase', fontSize: '0.65rem', py: 1.5 }}>Products</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 900, textTransform: 'uppercase', fontSize: '0.65rem', py: 1.5 }}>Price</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.isArray(productsInOrder) && productsInOrder.length > 0 ? (
                        productsInOrder.map((productName, index) => (
                          <TableRow key={index}>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{index + 1}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.8rem' }}>{productName}</Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.65rem' }}>Bespoke Artisanal Commission</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <input
                                type="text"
                                value={itemPrices[index] || ''}
                                onChange={(e) => setItemPrices({ ...itemPrices, [index]: e.target.value })}
                                style={{
                                  width: '90px',
                                  border: 'none',
                                  borderBottom: '1px solid rgba(181, 29, 110, 0.2)',
                                  textAlign: 'right',
                                  fontWeight: 900,
                                  fontFamily: 'inherit',
                                  fontSize: '0.8rem',
                                  outline: 'none',
                                  color: '#1a1a1a'
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>1</TableCell>
                          <TableCell sx={{ fontWeight: 800, fontSize: '0.8rem' }}>Custom Fine Art Masterpiece</TableCell>
                          <TableCell align="right">
                            <input
                              type="text"
                              style={{
                                width: '90px',
                                border: 'none',
                                borderBottom: '1px solid rgba(181, 29, 110, 0.2)',
                                textAlign: 'right',
                                fontWeight: 900,
                                fontFamily: 'inherit',
                                fontSize: '0.8rem',
                                outline: 'none'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              {/* Summary Section */}
              <Box sx={{ width: '100%' }}>
                <Box sx={{ p: 2, borderRadius: '16px', bgcolor: '#FAF9F6', border: '1px solid rgba(181, 29, 110, 0.05)' }}>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#4B5563' }}>Total Value</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>₹{order.order_amount?.toLocaleString() || 0}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#4B5563' }}>Advance Paid</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#059669' }}>- ₹{order.advance_amount?.toLocaleString() || 0}</Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Balance Due</Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#B51D6E' }}>₹{((order.order_amount || 0) - (order.advance_amount || 0)).toLocaleString()}</Typography>
                    </Box>
                  </Stack>
                </Box>
              </Box>

              {/* Static Footer */}
              <Box sx={{ mt: 2, textAlign: 'center', borderTop: '1px dashed #e5e7eb', pt: 4 }}>
                <Typography variant="body2" sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontStyle: 'italic', color: '#B51D6E', mb: 1, fontSize: '0.9rem' }}>
                  "Where art meets the heart of celebration."
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>Handcrafted with love at Ishqnama Atelier.</Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  Digitally Authorized Record • Non-Transferable Invoice
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handlePrintClose}>Cancel</Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrintConfirm} sx={{ background: '#B51D6E', borderRadius: '12px' }}>Print Now</Button>
        </DialogActions>
      </Dialog>

      {/* Tracking Modal */}
      <Dialog
        open={trackingModalOpen}
        onClose={() => {
          setTrackingModalOpen(false);
          setDelhiveryData(null);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '85vh', borderRadius: '24px' } }}
      >
        <DialogTitle sx={{ fontWeight: 900, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Real-time Status: {order.tracking_id}
          <IconButton onClick={() => {
            setTrackingModalOpen(false);
            setDelhiveryData(null);
          }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, height: 'calc(100% - 64px)', overflow: 'hidden' }}>
          {trackingLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
              <CircularProgress size={48} thickness={4} sx={{ color: 'primary.main' }} />
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>Fetching tracking details...</Typography>
            </Box>
          ) : delhiveryData ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Fixed Header Card */}
              <Box sx={{ mb: 3, px: 1 }}>
                <Card sx={{ 
                  borderRadius: '24px', 
                  border: 'none', 
                  boxShadow: '0 10px 30px rgba(0,0,0,0.04)',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8faff 100%)',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <Box sx={{ 
                    position: 'absolute', 
                    top: 0, right: 0, width: '150px', height: '150px', 
                    background: 'radial-gradient(circle, rgba(181, 29, 110, 0.03) 0%, transparent 70%)',
                    zIndex: 0
                  }} />
                  <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', mb: 0.5 }}>
                          Current Shipment Status
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 900, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main', boxShadow: '0 0 10px rgba(181, 29, 110, 0.4)' }} />
                          {delhiveryData.status}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <img 
                          src="https://upload.wikimedia.org/wikipedia/commons/2/23/Delhivery_Logo_%282019%29.png" 
                          alt="Delhivery" 
                          style={{ height: '16px', marginBottom: '4px' }} 
                        />
                        <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                          #{delhiveryData.waybill}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ mb: 3, opacity: 0.5 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ p: 1, borderRadius: '10px', bgcolor: 'rgba(181, 29, 110, 0.05)', color: 'primary.main' }}>
                            <CalendarTodayIcon sx={{ fontSize: 18 }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', display: 'block', fontSize: '0.65rem' }}>Expected Delivery</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.85rem' }}>{delhiveryData.expected_delivery ? dayjs(delhiveryData.expected_delivery).format('DD MMM YYYY') : 'N/A'}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ p: 1, borderRadius: '10px', bgcolor: 'rgba(0,0,0,0.05)', color: 'text.primary' }}>
                            <LocationOnIcon sx={{ fontSize: 18 }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', display: 'block', fontSize: '0.65rem' }}>Origin</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.85rem' }}>{delhiveryData.origin || 'N/A'}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ p: 1, borderRadius: '10px', bgcolor: 'rgba(0,0,0,0.05)', color: 'text.primary' }}>
                            <FmdGoodIcon sx={{ fontSize: 18 }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', display: 'block', fontSize: '0.65rem' }}>Destination</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.85rem' }}>{delhiveryData.destination || 'N/A'}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ p: 1, borderRadius: '10px', bgcolor: 'rgba(0,0,0,0.05)', color: 'text.primary' }}>
                            <LocalShippingIcon sx={{ fontSize: 18 }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', display: 'block', fontSize: '0.65rem' }}>Last Update</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.85rem' }}>{delhiveryData.last_update ? dayjs(delhiveryData.last_update).format('DD MMM, hh:mm A') : 'N/A'}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Box>

              {/* Scrollable Scan History */}
              <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  Journey History
                  <Chip label={`${delhiveryData.scans?.length || 0} Events`} size="small" sx={{ fontWeight: 800, fontSize: '0.65rem' }} />
                </Typography>
                
                <Box sx={{ position: 'relative', pl: { xs: 2, md: 4 } }}>
                  {/* Timeline Line */}
                  <Box sx={{ 
                    position: 'absolute', 
                    left: { xs: '25px', md: '41px' }, 
                    top: 20, bottom: 20, 
                    width: '2px', 
                    bgcolor: 'rgba(0,0,0,0.06)',
                    zIndex: 0
                  }} />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {delhiveryData.scans && delhiveryData.scans.length > 0 ? (
                      [...delhiveryData.scans].reverse().map((scan, idx) => {
                        const isLatest = idx === 0;
                        return (
                          <Box key={idx} sx={{ display: 'flex', gap: 3, position: 'relative', zIndex: 1 }}>
                            {/* Timeline Node */}
                            <Box sx={{ 
                              width: isLatest ? 18 : 12, 
                              height: isLatest ? 18 : 12, 
                              borderRadius: '50%', 
                              bgcolor: isLatest ? 'primary.main' : '#fff',
                              border: isLatest ? '4px solid #fff' : '2px solid rgba(0,0,0,0.15)',
                              boxShadow: isLatest ? '0 0 0 4px rgba(181, 29, 110, 0.1)' : 'none',
                              mt: 0.5,
                              flexShrink: 0
                            }} />

                            <Card sx={{ 
                              flex: 1, 
                              borderRadius: '16px', 
                              border: isLatest ? '1px solid rgba(181, 29, 110, 0.1)' : '1px solid rgba(0,0,0,0.03)',
                              boxShadow: isLatest ? '0 10px 25px rgba(181, 29, 110, 0.05)' : 'none',
                              background: isLatest ? '#fff' : 'rgba(255,255,255,0.5)',
                            }}>
                              <CardContent sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                  <Typography variant="body1" sx={{ fontWeight: 900, color: isLatest ? 'primary.main' : 'text.primary', fontSize: '0.95rem' }}>
                                    {scan.Scan}
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', bgcolor: 'rgba(0,0,0,0.03)', px: 1, py: 0.5, borderRadius: '6px' }}>
                                    {scan.ScanDateTime ? dayjs(scan.ScanDateTime).format('DD MMM, hh:mm A') : ''}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.8rem' }}>
                                  <LocationOnIcon sx={{ fontSize: 14 }} />
                                  {scan.ScannedLocation}
                                </Typography>
                                {scan.ScanDetail && (
                                  <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary', fontStyle: 'italic' }}>
                                    "{scan.ScanDetail}"
                                  </Typography>
                                )}
                              </CardContent>
                            </Card>
                          </Box>
                        );
                      })
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: '24px', border: '1px dashed rgba(0,0,0,0.1)' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>No scanning events recorded yet.</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : trackingUrl ? (
            <iframe src={trackingUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="Tracking" />
          ) : null}
        </DialogContent>
      </Dialog>



      {/* Delete Address confirmation */}
      <Dialog open={deleteAddressDialogOpen} onClose={() => setDeleteAddressDialogOpen(false)} PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Remove Destination?</DialogTitle>
        <DialogContent>
          <Typography>This will clear the shipping coordinates for this masterpiece.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDeleteAddressDialogOpen(false)}>Keep</Button>
          <Button variant="contained" color="error" onClick={handleDeleteAddress} disabled={deleteAddressLoading}>
            {deleteAddressLoading ? 'Removing...' : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dispatch Label Modal */}
      <Dialog open={labelModalOpen} onClose={() => setLabelModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
        <DialogTitle sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Dispatch Label
          <IconButton onClick={() => setLabelModalOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4, bgcolor: '#f5f5f5' }}>
          <Box id="label-content" sx={{
            width: '100%',
            maxWidth: '100%',
            bgcolor: '#FFFFFF',
            border: '2px solid #000',
            fontFamily: "'Inter', sans-serif",
            color: '#000',
            aspectRatio: '1/1.414', // A-series ratio
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            '@media print': {
              width: '100mm',
              height: '141mm',
              padding: '0',
              margin: '0',
            }
          }}>
            {/* LOGOS HEADER */}
            <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, borderBottom: '2px solid #000' }}>
              <Box component="img" src="/pink_logo.png" sx={{ height: 45 }} />
              <Box component="img" src="/namelogo.png" sx={{ height: 30 }} />
            </Box>

            {/* FROM & ORDER ID SECTION */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', borderBottom: '2px dashed #000' }}>
              <Box sx={{ p: 2, borderRight: '2px dashed #000' }}>
                <Typography sx={{ fontWeight: 900, fontSize: '0.65rem', textTransform: 'uppercase', mb: 0.5, color: '#4B5563' }}>Sender (From):</Typography>
                <Typography sx={{ fontWeight: 900, fontSize: '0.9rem', lineHeight: 1.2 }}>ISHQNAMA ART STUDIO</Typography>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Nathani Square 814, 8th floor, 70-78 kambekar street, Halai Memon Chotani Jamat Khana, Mumbai, Maharashtra, India, 400003</Typography>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Phone: +91-6393-342414</Typography>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Email: ishqnamaart@gmail.com</Typography>
              </Box>
              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9f9f9' }}>
                <Typography sx={{ fontWeight: 900, fontSize: '0.6rem', textTransform: 'uppercase', mb: 0.5 }}>Order Reference</Typography>
                <Typography sx={{ fontWeight: 950, fontSize: '1.2rem', color: '#B51D6E' }}>#{order.order_id}</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: '0.65rem' }}>{formatDate(order.created_at)}</Typography>
              </Box>
            </Box>

            {/* TO SECTION - MAIN */}
            <Box sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography sx={{ fontWeight: 900, fontSize: '0.85rem', textTransform: 'uppercase', mb: 1, color: '#B51D6E', letterSpacing: '0.05em' }}>Deliver To:</Typography>
              <Typography sx={{ fontWeight: 950, fontSize: '2rem', mb: 1, lineHeight: 1.1 }}>
                {address?.recipient_name || 'Valued Customer'}
              </Typography>
              <Stack spacing={0.5} sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#4B5563', minWidth: '85px' }}>CONTACT:</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>{address?.phone_number}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#4B5563', minWidth: '85px' }}>EMAIL:</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{address?.email || 'N/A'}</Typography>
                </Box>
              </Stack>
              <Box sx={{ p: 2.5, border: '2px solid #000', borderRadius: '4px', bgcolor: '#fcfcfc' }}>
                <Typography sx={{ fontWeight: 950, fontSize: '0.7rem', textTransform: 'uppercase', mb: 0.5, letterSpacing: '0.1em' }}>Shipping Address</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', lineHeight: 1.4 }}>
                  {address?.street_address},<br />
                  {address?.city}, {address?.state}<br />
                  <Typography component="span" sx={{ fontWeight: 950, fontSize: '1.8rem' }}>
                    {address?.postal_code}
                  </Typography>
                </Typography>
              </Box>
            </Box>

            {/* FOOTER - THANKS MESSAGE */}
            <Box sx={{ bgcolor: '#000', color: '#FFF', textAlign: 'center', p: 3 }}>
              <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: '1.2rem', fontStyle: 'italic', mb: 1.5 }}>
                Thank you for shopping with us!
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, borderTop: '1px solid rgba(255,255,255,0.2)', pt: 1.5 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', letterSpacing: '0.1em' }}>
                  WWW.ISHQNAMAART.IN
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', letterSpacing: '0.1em' }}>
                  INSTAGRAM: @ISHQNAMAART
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setLabelModalOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={() => handlePrintConfirm("label-content")} sx={{ background: '#B51D6E', borderRadius: '12px', fontWeight: 900, px: 3, color: '#FFF !important' }}>
            Print Label
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderDetails;
